import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, LogosSectionContent, SectionMargins } from "api/shop";
import {
  HeadingTagSelect,
  PaziaCheckbox,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

interface LogosSectionFormProps {
  onSubmit: (data: LogosSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: LogosSectionContent;
  isLoading?: boolean;
}

export const LogosSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: LogosSectionFormProps) => {
  const { uploadImages } = useShopImageUpload();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.LOGO_HEIGHT]: defaultValues?.logoHeight ?? 48,
      [FORM_NAMES.GRAYSCALE]: defaultValues?.grayscale ?? false,
      [FORM_NAMES.LOGOS]: defaultValues?.logos ?? [{ src: "", alt: "", href: "" }],
      [FORM_NAMES.BUTTONS]: defaultValues?.buttons ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: logoFields,
    append: appendLogo,
    remove: removeLogo,
  } = useFieldArray({
    control,
    name: FORM_NAMES.LOGOS,
  });

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BUTTONS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const uploadedLogos = await uploadImages(data.logos);
    const formattedData: LogosSectionFormValues = {
      animation: data.animation,
      header: { text: data.headerText, tag: data.headerTag as any },
      logos: uploadedLogos.map((logo) => ({
        src: logo.src,
        alt: logo.alt,
        ...(logo.href ? { href: logo.href } : {}),
      })),
      grayscale: data.grayscale ?? false,
      buttons: data.buttons.map((btn) => ({
        label: btn.label,
        variant: btn.variant,
        href: btn.href,
      })),
      margins: data.margins,
    };
    if (data.logoHeight) {
      formattedData.logoHeight = Number(data.logoHeight);
    }
    onSubmit(formattedData);
  };

  const addLogo = () => {
    appendLogo({ src: "", alt: "", href: "" });
  };

  const addButton = () => {
    appendButton({ label: "", variant: "secondary", href: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect name={FORM_NAMES.ANIMATION} label="Animacja" required options={ANIMATION_OPTIONS} />
          <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Nagłówek" required />
        </div>

        <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />

        <div className="grid items-end gap-3 md:grid-cols-2">
          <PaziaInput
            name={FORM_NAMES.LOGO_HEIGHT}
            label="Wysokość logotypów (px)"
            type="number"
            placeholder="48"
          />
          <PaziaCheckbox name={FORM_NAMES.GRAYSCALE} label="Wyszarzanie logotypów (kolor po najechaniu)" />
        </div>

        {/* Logos Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Logotypy</h4>
            <button
              type="button"
              onClick={addLogo}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj logo
            </button>
          </div>

          {logoFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak logotypów. Kliknij "Dodaj logo", aby dodać.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {logoFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Logo #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeLogo(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń logo"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="[&_img]:max-h-32">
                      <PaziaFileDropzone
                        name={`${FORM_NAMES.LOGOS}.${index}.src`}
                        label="Obraz"
                        type="image"
                        single
                        preview="image"
                      />
                    </div>
                    <PaziaInput name={`${FORM_NAMES.LOGOS}.${index}.alt`} label="Tekst alternatywny (alt)" required />
                    <PaziaInput name={`${FORM_NAMES.LOGOS}.${index}.href`} label="Link (otwiera w nowym oknie)" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Przyciski</h4>
            <button
              type="button"
              onClick={addButton}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj przycisk
            </button>
          </div>

          {buttonFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak przycisków. Kliknij "Dodaj przycisk", aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {buttonFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="flex-1">
                    <PaziaInput name={`${FORM_NAMES.BUTTONS}.${index}.label`} label="Etykieta" required />
                  </div>
                  <div className="w-40">
                    <ButtonVariantSelect name={`${FORM_NAMES.BUTTONS}.${index}.variant`} required />
                  </div>
                  <div className="flex-1">
                    <PaziaInput name={`${FORM_NAMES.BUTTONS}.${index}.href`} label="Link (href)" required />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeButton(index)}
                    className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń przycisk"
                  >
                    <MdDelete size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  ANIMATION: "animation",
  HEADER_TEXT: "headerText",
  HEADER_TAG: "headerTag",
  LOGO_HEIGHT: "logoHeight",
  GRAYSCALE: "grayscale",
  LOGOS: "logos",
  BUTTONS: "buttons",
} as const;

const logoSchema = yup.object({
  src: yup.mixed().required("Obraz jest wymagany"),
  alt: yup.string().required("Tekst alternatywny jest wymagany"),
  href: yup.string(),
});

const buttonSchema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  variant: yup.string().required("Wariant jest wymagany"),
  href: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.HEADER_TEXT]: yup.string().required("Nagłówek jest wymagany"),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.LOGO_HEIGHT]: yup
      .number()
      .transform((value, original) => (original === "" ? undefined : value))
      .optional()
      .positive("Wysokość musi być dodatnia"),
    [FORM_NAMES.GRAYSCALE]: yup.boolean().default(false),
    [FORM_NAMES.LOGOS]: yup.array().of(logoSchema).min(1, "Dodaj przynajmniej jedno logo").required(),
    [FORM_NAMES.BUTTONS]: yup.array().of(buttonSchema),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  headerText: string;
  headerTag: string;
  logoHeight?: number;
  grayscale?: boolean;
  logos: Array<{ src: string | File; alt: string; href?: string }>;
  buttons: Array<{ label: string; variant: "primary" | "secondary" | "outline"; href: string }>;
  margins: SectionMargins;
}

export interface LogosSectionFormValues {
  animation: string;
  header: { text: string; tag?: string };
  logoHeight?: number;
  grayscale?: boolean;
  logos: Array<{
    src: string;
    alt: string;
    href?: string;
  }>;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
  margins?: SectionMargins;
}
